import { motion, useReducedMotion } from 'motion/react';
import {
  EnvelopeSimpleIcon,
  ArrowsCounterClockwiseIcon,
  BroadcastIcon,
  XCircleIcon,
  CheckIcon,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import { formatDate, cn } from '@/lib/utils';
import { EASE_PREMIUM, MOTION } from '@/lib/motion';
import { RadarPing } from '@/components/motion/RadarPing';

type Tone = 'amber' | 'cobalt' | 'emerald' | 'rose';

const TONE: Record<
  Tone,
  { node: string; ring: string; ping: string; glow: string }
> = {
  amber: {
    node: 'bg-amber',
    ring: 'ring-amber/20',
    ping: 'bg-amber',
    glow: 'dark:shadow-[0_0_10px_-1px_var(--gl-amber)]',
  },
  cobalt: {
    node: 'bg-cobalt',
    ring: 'ring-cobalt/20',
    ping: 'bg-cobalt',
    glow: 'dark:shadow-[0_0_10px_-1px_var(--gl-cobalt)]',
  },
  emerald: {
    node: 'bg-emerald',
    ring: 'ring-emerald/20',
    ping: 'bg-emerald',
    glow: 'dark:shadow-[0_0_10px_-1px_var(--gl-emerald)]',
  },
  rose: {
    node: 'bg-destructive',
    ring: 'ring-destructive/20',
    ping: 'bg-destructive',
    glow: 'dark:shadow-[0_0_10px_-1px_var(--destructive)]',
  },
};

type StageState = 'completed' | 'active' | 'pending';

const FILL_PERCENT: Record<StageState, number> = {
  completed: 100,
  active: 50,
  pending: 0,
};

interface DisplayStage {
  label: string;
  icon: PhosphorIcon;
  tone: Tone;
  statusText: string;
  state: StageState;
  noPing?: boolean;
  fullBar?: boolean;
}

function buildStages(status: string, endDate?: string): DisplayStage[] {
  const isDeclined = status === '' || status === 'canceled';
  const isPending = status === 'new' || status === 'reviewing';
  const isNegotiating =
    status === 'new-counter' || status === 'counter-in-review';
  const isLive =
    status === 'accepted' || status === 'active' || status === 'paused';
  const isFinished = status === 'ended';

  const pendingState: StageState = isPending ? 'active' : 'completed';

  const negotiatingState: StageState = isPending
    ? 'pending'
    : isNegotiating
      ? 'active'
      : 'completed';

  const liveState: StageState = isFinished
    ? 'completed'
    : isLive
      ? 'active'
      : 'pending';

  const liveStatusText = isFinished
    ? endDate
      ? `Ended ${formatDate(endDate)}`
      : 'Completed'
    : isLive
      ? endDate
        ? `Ends ${formatDate(endDate)}`
        : 'In progress'
      : 'Not started';

  const pendingStage: DisplayStage = {
    label: 'Pending',
    icon: EnvelopeSimpleIcon,
    tone: 'amber',
    statusText:
      pendingState === 'active' ? 'Awaiting your response' : 'Completed',
    state: pendingState,
  };

  const negotiatingStage: DisplayStage = {
    label: 'Negotiating',
    icon: ArrowsCounterClockwiseIcon,
    tone: 'cobalt',
    statusText:
      negotiatingState === 'active'
        ? status === 'new-counter'
          ? 'Counter received'
          : 'Counter in review'
        : negotiatingState === 'completed'
          ? 'Completed'
          : 'Not started',
    state: negotiatingState,
  };

  if (isDeclined) {
    return [
      pendingStage,
      negotiatingStage,
      {
        label: 'Declined',
        icon: XCircleIcon,
        tone: 'rose',
        statusText: 'Offer declined',
        state: 'active',
        noPing: true,
        fullBar: true,
      },
    ];
  }

  return [
    pendingStage,
    negotiatingStage,
    {
      label: 'Live',
      icon: BroadcastIcon,
      tone: 'emerald',
      statusText: liveStatusText,
      state: liveState,
    },
  ];
}

function StageColumn({
  stage,
  index,
  total,
}: {
  stage: DisplayStage;
  index: number;
  total: number;
}) {
  const reduced = useReducedMotion();
  const { label, icon: Icon, tone, statusText, state, noPing, fullBar } = stage;
  const t = TONE[tone];

  return (
    <li
      aria-current={state === 'active' ? 'step' : undefined}
      className={cn(
        'flex flex-col gap-3.5',
        state === 'pending' && 'opacity-50'
      )}
    >
      <span className="sr-only">{`Step ${index + 1} of ${total}: ${label}, ${state}.`}</span>

      <div className="flex items-center gap-2.5">
        <div className="relative flex items-center justify-center" aria-hidden>
          {state === 'active' && !noPing && <RadarPing className={t.ping} />}
          <span
            className={cn(
              'relative z-10 flex size-8 items-center justify-center rounded-full transition-colors',
              state === 'active' && cn(t.node, 'text-white ring-4', t.ring),
              state === 'completed' && 'bg-muted text-foreground',
              state === 'pending' &&
                'border border-border bg-card text-muted-foreground'
            )}
          >
            {state === 'completed' ? (
              <CheckIcon weight="bold" className="size-[15px]" />
            ) : (
              <Icon weight="fill" className="size-[15px]" />
            )}
          </span>
        </div>
        <span
          className={cn(
            'text-sm font-semibold',
            state === 'active' ? 'text-foreground' : 'text-muted-foreground'
          )}
        >
          {label}
        </span>
      </div>

      <div
        className="w-full overflow-hidden rounded-full border border-border bg-card p-[5px]"
        aria-hidden
      >
        <motion.div
          className={cn(
            'relative h-1 overflow-hidden rounded-full transition-colors',
            state === 'active' && cn(t.node, t.glow),
            state === 'completed' && 'bg-muted-foreground/40'
          )}
          initial={reduced ? false : { width: 0 }}
          animate={{ width: `${fullBar ? 100 : FILL_PERCENT[state]}%` }}
          transition={{
            duration: MOTION.meter,
            ease: EASE_PREMIUM,
            delay: 0.1,
          }}
        >
          {state === 'active' && !reduced && (
            <motion.span
              aria-hidden
              className="absolute inset-y-0 w-1/3 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.7),transparent)]"
              initial={{ x: '-150%' }}
              animate={{ x: '350%' }}
              transition={{
                duration: 1.8,
                ease: 'easeInOut',
                repeat: Infinity,
                repeatDelay: 0.9,
              }}
            />
          )}
        </motion.div>
      </div>

      <span
        className={cn(
          'text-xs font-medium leading-4',
          state === 'active'
            ? 'text-foreground font-semibold'
            : 'text-muted-foreground'
        )}
      >
        {statusText}
      </span>
    </li>
  );
}

interface OfferProgressProps {
  status: string;
  endDate?: string;
}

export function OfferProgress({ status, endDate }: OfferProgressProps) {
  const stages = buildStages(status, endDate);

  return (
    <div className="bg-secondary border rounded-xl p-8 shadow-premium">
      <ol
        role="list"
        aria-label="Offer progress"
        className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-3"
      >
        {stages.map((stage, i) => (
          <StageColumn
            key={stage.label}
            stage={stage}
            index={i}
            total={stages.length}
          />
        ))}
      </ol>
    </div>
  );
}
